import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, shareReplay, Subject } from 'rxjs';
import { GetterService } from './getter-service.service';
import { CartProdut, CartRecipies } from 'src/Model/Checkout-Interfaces';
import { ApiService } from './api.service';
import { Category, SubCategory } from 'src/Model/Product-Interfaces';
import { brandUrl } from 'src/app/brand/brand-context';

/** A category (and sub-category) picked in the legacy header's Stellar side drawer. */
interface SideMenuData {
  cat: Category;
  subCat: SubCategory;
}

@Injectable({
  providedIn: 'root',
})
export class RequestService {
  redirectToComponent: string;
  /* legacy design: online's checkout button, and the Stellar side drawer
     (side-drawer) driving the products / store / shop categories */
  headerCheckoutBtn = new Subject<null>();
  sideCatChanged = new Subject<SideMenuData>();
  sideCatClicked = new Subject<SideMenuData>();
  resetSideCats = new Subject<void>();
  cartCounter$ = new BehaviorSubject<number>(0);
  cartTotal$ = new BehaviorSubject<number>(0);
  postCode: string = ''; // to populate checkout postcode from menu
  /* Delivery Type */
  storageChange = new Subject<void>();

  /* For updating delivery type from header component to other components */
  deliveryType$: BehaviorSubject<'Home' | 'Store' | 'EatIn' | ''> =
    new BehaviorSubject('');

  sub: any;

  constructor(
    private _http: HttpClient,
    private _local: GetterService,
    private apiService: ApiService
  ) {
    this.updateCartCounter();
  }

  getRequest(url): Observable<any> {
    return this._http.get(environment.apiUrl + url).pipe(shareReplay(1));
  }

  deleteRequest(url): Observable<any> {
    return this._http.delete(environment.apiUrl + url).pipe(shareReplay(1));
  }

  postRequest(url, params, token = ''): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: token,
    });

    return this._http
      .post<any>(environment.apiUrl + url, params, {
        headers: headers,
      })
      .pipe(shareReplay(1));
  }

  putRequest(url, params): Observable<any> {
    return this._http
      .put(environment.apiUrl + url, params)
      .pipe(shareReplay(1));
  }

  isLoggedIn() {
    return !!this._local.get('access_token');
  }

  isGuestLoggedIn() {
    return !!this._local.getSessionItem('access_token');
  }

  saveProductCartItems(products: CartProdut[]) {
    this._local.set('cartProductItems', JSON.stringify(products));
    this.updateCartCounter();
    this.storageChange.next();
  }

  saveRecipeCartItems(recipes) {
    this._local.set('cartRecipeItems', JSON.stringify(recipes));
    this.storageChange.next();
    this.updateCartCounter();
  }

  /**
   * Whether two extras selections describe the same cart line: the same extras
   * (group + title + price) with the same quantity of each, in any order. A
   * different extra quantity is a different line, so the item is added rather
   * than merged. Price is part of the identity because a group can repeat a
   * title at different prices (e.g. "DONATION" £1 / £2 / £5) — without it a
   * £1 line and a £5 line merged and the total kept the first line's price.
   */
  sameExtras(a: any[] | null | undefined, b: any[] | null | undefined): boolean {
    const left = a ?? [];
    const right = b ?? [];
    if (left.length !== right.length) return false;

    const signature = (extras: any[]) =>
      extras
        .map(
          (extra) =>
            `${extra?.Key ?? extra?.cat ?? ''}|${
              extra?.Title ?? extra?.Item ?? ''
            }|${Number(extra?.Price ?? extra?.ExtraPrice ?? 0)}|${Number(
              extra?.Quantity ?? 1
            )}`
        )
        .sort()
        .join('\n');

    return signature(left) === signature(right);
  }

  /**
   * A page URL for router.navigate* in the form this visit uses:
   * 'primebarbers/menu' on the shared host (brand in the path), plain
   * 'menu' on a brand's own host — see brand/brand-context.ts.
   */
  setUrl(pageName) {
    return brandUrl(pageName);
  }

  scrollToTop() {
    window.scrollTo(0, 0);
  }

  getRecipiesItemsForSideCart() {
    const recipes = this._local.get('cartRecipeItems')
      ? JSON.parse(this._local.get('cartRecipeItems'))
      : [];
    return recipes;
  }

  getProductItemsForSideCart() {
    const products = this._local.get('cartProductItems')
      ? JSON.parse(this._local.get('cartProductItems'))
      : [];
    return products;
  }

  getCartRecipeItems(): CartRecipies[] {
    const recipes = [];
    let recipeCart = JSON.parse(this._local.get('cartRecipeItems'));
    recipeCart?.forEach((value) => {
      let extraArray: any[] = [];
      if (
        value.RecipeExtraObject != null &&
        value.RecipeExtraObject.length != 0
      ) {
        value.RecipeExtraObject.forEach((extra) => {
          extraArray.push({
            Image: extra.Image,
            Key: extra.Key,
            Price: extra.Price,
            Quantity: extra.Quantity,
            Title: extra.Title,

            ExtraTitle: extra.Title,
            ExtraQuantity: extra.Quantity,
            ExtraPrice: extra.Price,

            RecipeOldPrice: extra.RecipeOldPrice,
            RecipeDiscountType: extra.RecipeDiscountType,
            RecipeDiscountAmount: extra.RecipeDiscountAmount,
          });
        });
      }
      recipes.push({
        ItemId: value.RecipeId,
        Quantity: value.RecipeQuantity,
        TotalPrice: value.isPromotionItem ? 0 : value.RecipePrice,
        IsDiscountedItem: value.IsDiscountedItem
          ? value.IsDiscountedItem
          : false,
        SocialDiscountItem: value?.socialDiscountItem
          ? value?.socialDiscountItem
          : false,

        CategoryTitle: value.CategoryTitle,
        ForCollection: value.ForCollection,
        ForDelivery: value.ForDelivery,
        ForDineIn: value.ForDineIn,
        ForEatIn: value.ForEatIn,
        RecipeDiscountAmount: value.RecipeDiscountAmount,
        RecipeDiscountType: value.RecipeDiscountType,
        RecipeExtraObject: extraArray,
        RecipeId: value.RecipeId,
        RecipeImage: value.RecipeImage,
        RecipeMaxBuyAllowed: value.RecipeMaxBuyAllowed,
        RecipeOldPrice: value.RecipeOldPrice,
        RecipePrice: value.RecipePrice,
        RecipeQuantity: value.RecipeQuantity,
        RecipeTitle: value.RecipeTitle,
        recipeTotal: value.recipeTotal,
        isPromotionItem: value.isPromotionItem,
        PromotionItemId: value.PromotionItemId,
        ExtraDetail: JSON.stringify(extraArray),
      });
    });
    return recipes;
  }

  getProductCartItems() {
    const products = [];
    let productCart = JSON.parse(this._local.get('cartProductItems'));
    productCart?.forEach((value) => {
      let extraArray: any[] = [];
      if (
        value.ProductExtraObject != null &&
        value.ProductExtraObject.length != 0
      ) {
        value.ProductExtraObject.forEach((extra) => {
          extraArray.push({
            ExtraTitle: extra.Title,
            ExtraQuantity: extra.Quantity,
            ExtraPrice: extra.Price,
          });
        });
      }
      products.push({
        ItemId: value.ProductId,
        Quantity: value.ProductQuantity,
        TotalPrice: value.ProductOldPrice,
        IsDiscountedItem: value.IsDiscountedItem
          ? value.IsDiscountedItem
          : false,
        SocialDiscountItem: value?.socialDiscountItem
          ? value?.socialDiscountItem
          : false,
        ExtraDetail: JSON.stringify(extraArray),
      });
    });
    return products;
  }

  updateCartCounter() {
    this.sub = this.apiService.getWebcustomer$.subscribe((res) => {
      if (!res) return;

      let cartProductItems: CartProdut[] = this._local.get('cartProductItems')
        ? JSON.parse(this._local.get('cartProductItems'))
        : [];
      let cartRecipeItems: CartRecipies[] = this._local.get('cartRecipeItems')
        ? JSON.parse(this._local.get('cartRecipeItems'))
        : [];

      let productsCounter = 0;
      let reciepsCounter = 0;
      let total = 0;
      if (res.TemplateData.RecipesBrand) {
        cartProductItems.forEach((product) => {
          productsCounter += product.ProductQuantity;
          total += product.ProductNewPrice * product.ProductQuantity;
        });

        cartRecipeItems.forEach((recipe) => {
          reciepsCounter += recipe.RecipeQuantity;
          total += recipe.RecipePrice * recipe.RecipeQuantity;
        });
        this.cartTotal$.next(total);
      } else {
        productsCounter = cartProductItems.length;
        reciepsCounter = cartRecipeItems.length;
      }

      setTimeout(() => {
        this.cartCounter$.next(reciepsCounter + productsCounter);
      }, 1);

      if (this.sub) {
        this.sub.unsubscribe();
      }
    });
  }
}
